import { useEffect, useState } from "react"

import { App, Button, Modal, Spin, Typography } from "antd"
import cls from "classnames"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import CreditRequestForm from "../../../../Components/Forms/CreditRequestForm"
import Icon from "../../../../Icon"
import { extendedAccessRoles } from "../../../../Utils/permission"
import Creators from "../../reducer"
import { useCreditsPageQuery } from "../../useCreditsPageQuery"
import { CreditRequestInstructions } from "../CreditRequestInstructions"

import instructionsStyles from "../CreditRequestInstructions/CreditRequestInstructions.module.less"

const { Text } = Typography

export const CreditRequestPreview = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const { openCreditRequestID, business_account_id, attach, openCreditResubmit } =
    useCreditsPageQuery(search)

  const { fetching, creditDetails, saveFetching, saveResult } = useSelector(
    (state) => state.credits
  )
  const isMobile = useSelector((state) => state.nav.isMobile)
  const { userInfo, isAdmin } = useSelector((state) => state.auth)
  const baRole = useSelector((state) => state.auth.userInfo?.ba_role?.id)
  const role = useSelector((state) => state.auth.userInfo?.role?.id)
  const businessAccount = useSelector((state) => state.user.businessAccount)
  const [selectedReason, setSelectedReason] = useState(null)

  const open = !!openCreditRequestID && !!creditDetails
  const isRejected = creditDetails?.enabled_for_resubmit?.status
  const isDisabledSubmitBtn = !isAdmin && !extendedAccessRoles?.includes(baRole ?? role)
  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const connectedPaymentProcessors = businessAccount?.connected_payment_processors
  const getInstructionProcessor = (processors) => {
    if (processors?.length === 1 && processors[0] === "stripe") return "stripe"
    if (processors?.length === 1 && processors[0] === "shopify") return "shopify"
    return "other"
  }
  const instructionProcessor = getInstructionProcessor(connectedPaymentProcessors)

  useEffect(() => {
    if (openCreditRequestID) {
      dispatch(Creators.getCreditDetailsRequest(openCreditRequestID))
    }
  }, [openCreditRequestID])

  useEffect(() => {
    if (saveResult === "attach_proof") {
      handleCloseModal()
      notification.success({
        message: t("Credit request updated"),
        description: t(
          "Thank you for reaching out! Your credit request has been updated successfully."
        ),
        ...notificationConfig
      })
    }
  }, [saveResult])

  const handleCloseModal = () => {
    if (business_account_id) {
      navigate(`${pathname}?business_account_id=${business_account_id}`, { replace: true })
    } else {
      navigate(pathname, { replace: true })
    }
    dispatch(Creators.creditsSetProp("creditDetails", null))
  }

  const handleSubmitForm = (data) => {
    const isEvidenceSubmit =
      attach === "true" && Array.isArray(data?.file_list) && data.file_list.length > 0
    const submitData = isEvidenceSubmit ? { ...data, as_evidence: true } : data

    if (isRejected) {
      dispatch(
        Creators.resubmitCreditRequestRequest({
          id: creditDetails?.id || openCreditResubmit,
          data: submitData
        })
      )
    } else {
      dispatch(
        Creators.attachProofCreditRequest({
          id: creditDetails?.id || openCreditRequestID,
          ...submitData
        })
      )
    }
  }

  const title = attach ? "Attach proof for credit request" : "Credit request"

  return (
    <Modal
      open={open}
      centered={!isMobile}
      width={isMobile ? 442 : 842}
      title={isMobile ? title : null}
      footer={null}
      closable={isMobile}
      onCancel={handleCloseModal}
      destroyOnClose
      classNames={{ content: cls("clear-bg", !isMobile && instructionsStyles.splitModalContent) }}
      getContainer={() => document.getElementById("global-wrap")}
      closeIcon={<Icon type="cross_small" />}
    >
      {isMobile ? (
        <Spin size="large" spinning={saveFetching}>
          <CreditRequestForm
            initialValues={creditDetails}
            onSubmit={handleSubmitForm}
            onClose={handleCloseModal}
            isDisabledSubmitBtn={isDisabledSubmitBtn}
            onReasonChange={setSelectedReason}
          />
        </Spin>
      ) : (
        <div className={instructionsStyles.splitModalBody}>
          <div className={instructionsStyles.formContainer}>
            <div className={cls("ant-modal-header", instructionsStyles.customModalHeader)}>
              <div className="ant-modal-title">{title}</div>
              <Button
                type="button"
                className={cls(instructionsStyles.customModalClose)}
                onClick={handleCloseModal}
                variant="outlined"
                icon={<Icon type="cross_small" />}
              />
            </div>
            <Spin size="large" spinning={saveFetching}>
              <CreditRequestForm
                initialValues={creditDetails}
                onSubmit={handleSubmitForm}
                onClose={handleCloseModal}
                isDisabledSubmitBtn={isDisabledSubmitBtn}
                onReasonChange={setSelectedReason}
              />
            </Spin>
          </div>
          <CreditRequestInstructions
            reason={selectedReason || undefined}
            paymentProcessor={instructionProcessor}
          />
        </div>
      )}
    </Modal>
  )
}
